import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { capitalize, get } from 'lodash-es';

import Form from 'reactstrap/lib/Form';
import { connect } from 'react-redux';
import { clearProfileData, deleteProfile } from 'client/actions/profile';
import { venomHistory } from 'client/utils/history/venom-history';

import { logger } from 'client/utils/logger/logger';
import { useToggle } from 'site-modules/shared/hooks/use-toggle';
import { useStateCallback } from 'site-modules/shared/hooks/use-state-callback';
import {
  auth,
  getEmailProvider,
  reauthenticateWithCredential,
  reauthenticateWithPopup,
} from 'site-modules/shared/components/profile/firebase-auth';
import {
  fireDeleteConfirmationAction,
  fireDeletionFailed,
  fireDeletionSuccess,
} from 'client/engagement-handlers/profile-engagement-handler/profile-engagement-handler';
import { remove } from 'site-modules/shared/components/profile/idm/idm';
import { validation } from 'site-modules/shared/components/form-validation/validation';
import { settingsWrapperComponent } from 'site-modules/profile/components/settings/settings-wrapper/settings-wrapper';
import { formValidation } from 'site-modules/shared/components/form-validation/form-validation';
import { firebaseValidation } from 'site-modules/shared/components/profile/firebase-validation';
import { FormField } from 'site-modules/shared/components/form-field/form-field';
import { ProfileFormErrors } from 'site-modules/shared/components/profile/profile-form-errors/profile-form-errors';
import { AUTH_PROVIDERS, PROVIDERS_MAPPING } from 'site-modules/shared/components/profile/auth-providers';
import { PASSWORD_MAX_LENGTH } from 'site-modules/shared/components/profile/smart-sign/helper';

import { DeleteConfirmation } from './delete-confirmation';
import './delete-account.scss';

function DeleteAccountUI({
  fields = {},
  fieldsOrder = [],
  fieldRef,
  validate,
  validationErrors = {},
  firebaseError = null,
  parseFirebaseError = null,
  isEditMode = false,
  profileData,
  callDeleteProfile,
  callClearProfileData,
}) {
  const [isSpinnerActive, setSpinnerActive] = useToggle(false);
  const [accountDeletionSuccess, setAccountDeletionSuccess] = useStateCallback(false);
  const [accountDeletionFailed, setAccountDeletionFailed] = useState(false);

  const currentUser = auth().currentUser;
  const providerName = PROVIDERS_MAPPING[get(currentUser, 'providerData[0].providerId', 'google')];
  const emailProvider = getEmailProvider();

  const email = get(profileData, 'identifiers.email');
  const isValidEmail = !('email' in validationErrors);
  const isValidPassword = !('password' in validationErrors);

  function deletionFailed(isFirebaseError) {
    const coreId = get(profileData, 'identifiers.coreId');
    fireDeletionFailed(coreId);
    setAccountDeletionFailed(!isFirebaseError);
    setSpinnerActive(false);
  }

  function handleFirebaseCall(promise) {
    return promise.catch(error => {
      deletionFailed(true);
      parseFirebaseError(error);
      logger.error(error);
      return Promise.reject(error);
    });
  }

  function reauthenticateCredentials() {
    if (validate()) {
      const emailValue = fields.email.value();
      const passwordValue = fields.password.value();
      setSpinnerActive(true);

      return handleFirebaseCall(reauthenticateWithCredential(emailValue, passwordValue));
    }
    return Promise.resolve();
  }

  function reauthenticateSocial(type) {
    return () => {
      setSpinnerActive(true);
      const { provider, profileScope, providerId } = AUTH_PROVIDERS[type];
      return handleFirebaseCall(reauthenticateWithPopup(provider, profileScope, providerId));
    };
  }

  function removeProcess() {
    const coreId = get(profileData, 'identifiers.coreId');
    fireDeleteConfirmationAction(coreId);
    return remove()
      .then(async result => {
        if (result.responseStatus === 'OK') {
          callDeleteProfile();
          fireDeletionSuccess(coreId);
          setSpinnerActive(false);
          setAccountDeletionSuccess(true, () => {
            setTimeout(() => {
              callClearProfileData();
              venomHistory.push('/');
            }, 10000);
          });
        } else {
          deletionFailed();
        }
      })
      .catch(err => {
        logger.error(`Delete account: ${err}`);
        deletionFailed();
      });
  }

  const ErrorPanel = (
    <div className="mt-1 small">
      <ProfileFormErrors fieldsOrder={fieldsOrder} validationErrors={validationErrors} firebaseError={firebaseError} />
      {accountDeletionFailed && (
        <div className="text-danger">
          Error: Account deletion not successful. Please try again later or{' '}
          <a className="text-primary-darker" href="https://help.edmunds.com/hc/en-us/requests/new?ticket_form_id=2343">
            contact our support team
          </a>
          .
        </div>
      )}
    </div>
  );

  return (
    isEditMode && (
      <Fragment>
        {accountDeletionSuccess && (
          <div className="mb-2" data-test="success">
            <p>
              Your deletion request has been successfully submitted.{' '}
              <span className="fw-bold">Account deletion can take up to a day to process.</span>
            </p>
            <p>
              You will be redirected to{' '}
              <a className="text-primary-darker" href="/">
                Edmunds.com
              </a>{' '}
              shortly
            </p>
          </div>
        )}
        {!accountDeletionSuccess && (
          <Fragment>
            <div className="mb-2">Please confirm your identity to delete your account</div>
            {emailProvider ? (
              <Form id="smart-sign-email-form" noValidate>
                <FormField
                  className="d-flex flex-column flex-md-row align-items-md-center"
                  labelClassName="medium mb-0_25"
                  type="email"
                  autoComplete="email"
                  name="email"
                  id="reauth-email"
                  label="Email *"
                  ref={field => fieldRef('email', field)}
                  defaultValue={email}
                  isValid={isValidEmail}
                  aria-required
                  aria-invalid={!isValidEmail}
                  aria-describedby="profile-form-errors"
                />
                <FormField
                  className="d-flex flex-column flex-md-row align-items-md-center"
                  labelClassName="medium mb-0_25"
                  type="password"
                  name="password"
                  id="reauth-password"
                  label="Password *"
                  ref={field => fieldRef('password', field)}
                  maxLength={PASSWORD_MAX_LENGTH}
                  isValid={isValidPassword}
                  aria-required
                  aria-invalid={!isValidEmail}
                  aria-describedby="profile-form-errors"
                />
                {ErrorPanel}
                <div className="mt-1 text-end">
                  <DeleteConfirmation
                    preOpenCallback={reauthenticateCredentials}
                    acceptCallback={removeProcess}
                    cancelCallback={setSpinnerActive}
                    buttonText="Confirm and Delete"
                    buttonClassName="size-16 btn-outline-primary-b"
                    isSpinnerActive={isSpinnerActive}
                  />
                </div>
              </Form>
            ) : (
              <Fragment>
                {ErrorPanel}
                <DeleteConfirmation
                  preOpenCallback={reauthenticateSocial(providerName)}
                  acceptCallback={removeProcess}
                  cancelCallback={setSpinnerActive}
                  buttonText={`Continue with ${capitalize(providerName)}`}
                  buttonClassName={`${providerName}-button text-white text-transform-none border-0`}
                  buttonIcon={`icon-${AUTH_PROVIDERS[providerName].iconName}`}
                  isSpinnerActive={isSpinnerActive}
                />
              </Fragment>
            )}
          </Fragment>
        )}
      </Fragment>
    )
  );
}

DeleteAccountUI.propTypes = {
  profileData: PropTypes.shape({}).isRequired,
  callDeleteProfile: PropTypes.func.isRequired,
  callClearProfileData: PropTypes.func.isRequired,
  fieldRef: PropTypes.func.isRequired,
  fields: PropTypes.objectOf(
    PropTypes.shape({
      email: PropTypes.object,
      password: PropTypes.object,
    })
  ),
  fieldsOrder: PropTypes.arrayOf(PropTypes.string),
  validate: PropTypes.func.isRequired,
  validationErrors: PropTypes.objectOf(PropTypes.string),
  parseFirebaseError: PropTypes.func,
  firebaseError: PropTypes.shape({}),
  isEditMode: PropTypes.bool,
};

const validators = {
  email: {
    test: validation.validateEmail,
    errorText: 'Please enter your correct email address',
  },
  password: {
    test: validation.validateMinLength,
    errorText: 'Your password must be at least 7 characters',
    minLength: 7,
  },
};

const mapDispatchToProps = dispatch => ({
  callDeleteProfile: () => dispatch(deleteProfile()),
  callClearProfileData: () => dispatch(clearProfileData()),
});

const DeleteAccountForm = connect(null, mapDispatchToProps)(DeleteAccountUI);
export const DeleteAccount = settingsWrapperComponent(
  formValidation(firebaseValidation(DeleteAccountForm), validators)
);
